// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import formatEuro from '../../helpers/formatEuro'
import useParticipationParameters from '../../hooks/useParticipationParameters'
import useMsiMonetaryTotal from '../../hooks/useMsiMonetaryTotal'
import useMaterialValue from '../../hooks/useMaterialValue'
import sumCurrentSocietalValue from '../../helpers/participationHelpers/sumCurrentSocietalValue'
import useParticipationBirthDates from '../../hooks/useParticipationBirthDates'
import { isPostsModel } from '../../helpers/participationHelpers/calculateParticipationImpact'
import dataSourceErrorMessage from '../../helpers/dataSourceErrorMessage'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/**
 * Compact KPI tile: the live total Maatschappelijke waarde — three streams,
 * computed separately (double-counting guard) and only added here; the detail
 * page shows them as distinct sections. The trend sparkline was deprecated and
 * removed.
 *
 *   1. participatieladder over actieve deelnemers
 *   2. MSI-effect-monetisatie
 *   3. maatschappelijke besparing van materialen en coachadviezen
 *
 * Alle drie zonder periodegrenzen: deze kaart toont de opgebouwde waarde sinds
 * de start, niet één boekjaar. Het kengetal Impact per euro gebruikt dezelfde
 * stromen wél boekjaar-gescopet.
 *
 * Van de materialen telt alleen de maatschappelijke besparing mee — de
 * besparing op de energierekening is geld van de bewoner en de bespaarde CO2
 * heeft een eigen eenheid.
 * AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 1.3
 */
const DashboardParticipationValueCard = () => {
    const history = useHistory()
    const { parameters, loading: parametersLoading, error: parametersError } = useParticipationParameters()
    const personas = useFirestoreGeneral('Personas', 'Status', 'active')
    const { total: msiTotal, loading: msiLoading, error: msiError } = useMsiMonetaryTotal()
    const { value: materialValue, loading: materialLoading, error: materialError } = useMaterialValue()
    // Geboortedatums voor de leeftijdsovergang (alleen onder 2026.3).
    const { birthDates, error: birthDatesError } = useParticipationBirthDates(isPostsModel(parameters))

    // Laden is een eigen toestand, geen ontbrekende waarde. Alle drie de
    // stromen (plus de deelnemers) tellen mee: zolang er één onderweg is, is
    // het totaal 'nog niet bekend'. De hook geeft '' zolang hij laadt — een
    // lege array is een geladen leeg resultaat en betekent iets anders.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loading = parametersLoading === true
        || personas === ''
        || msiLoading === true
        || materialLoading === true
        || birthDates === null

    // null until the model parameters have loaded — fail visibly rather than
    // recompute the total against bundled defaults.
    const participationTotal = useMemo(
        () => (parameters && birthDates ? sumCurrentSocietalValue(Array.isArray(personas) ? personas : [], parameters, birthDates) : null),
        [personas, parameters, birthDates],
    )

    // Alle drie de stromen moeten er zijn voordat er iets wordt getoond — een
    // deeltotaal doet zich stilzwijgend als het geheel voor.
    const total = participationTotal === null || msiTotal === null || materialValue === null
        ? null
        : participationTotal + msiTotal + materialValue

    // De melding noemt de stroom die wegviel. Eén tekst voor alle drie wijst
    // bij twee ervan de verkeerde kant op — zie de helper.
    const errorMessage = dataSourceErrorMessage([
        ['rekenparameters', parametersError],
        ['effectwaarden', msiError],
        ['materiaalwaarden', materialError],
        ['geboortedatums', birthDatesError],
    ])

    return (
        <div
            className='participation-value-card participation-value-card--clickable'
            role='button'
            tabIndex={0}
            onClick={() => history.push(`/${client}/maatschappelijke-waarde`)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    history.push(`/${client}/maatschappelijke-waarde`)
                }
            }}
        >
            <StaticCardLabel title='Maatschappelijke waarde' />
            {errorMessage ? (
                <p className='participation-value-error'>{errorMessage}</p>
            ) : (
                <KpiValue loading={loading} value={total === null ? null : formatEuro(total)} />
            )}
            <span className='participation-value-more'>Meer info →</span>
        </div>
    )
}

export default DashboardParticipationValueCard
